import { createContext, useContext, useMemo } from 'react';

import {
  useGetReqsReviewConfig,
  type ReqsReviewConfigItem,
  type ValueTypeToAvailableUnits,
  type ResolutionConfig,
  type ResolutionConfigMap,
  ResolutionConfigItemType,
  ResolutionConfigItem,
} from '@frontend/api';
import { type CascaderSubmenu, EMPTY_SUBMENU } from '@frontend/components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';

import type { SignalSelectOptionType } from './components/SignalSelect';
import { createCascaderOptions } from './utils/cascader';
import { createPathConfigMaps } from './utils/configMap';

type ReqsReviewConfigContextType = {
  configMap: Record<string, ReqsReviewConfigItem>;
  labelPathMap: Record<string, string>;
  cascaderOptions: CascaderSubmenu<SignalSelectOptionType>;
  valueTypeToAvailableUnits: ValueTypeToAvailableUnits;
  isLoading: false;
  resolutionConfig: ResolutionConfig & { mapped: ResolutionConfigMap };
  getResolutionConfigItem: (
    resolutionType: ResolutionConfigItemType,
  ) => ResolutionConfigItem | null;
};

type ReqsReviewConfigLoadingType = {
  // By defining this value as a literal true, we can use it as a check against all other null values
  isLoading: true;
  configMap: null;
  labelPathMap: null;
  cascaderOptions: CascaderSubmenu<SignalSelectOptionType>;
  valueTypeToAvailableUnits: null;
  resolutionConfig: null;
  getResolutionConfigItem: (
    resolutionType: ResolutionConfigItemType,
  ) => ResolutionConfigItem | null;
};

const DEFAULT_VALUE: ReqsReviewConfigLoadingType = {
  isLoading: true,
  configMap: null,
  labelPathMap: null,
  cascaderOptions: EMPTY_SUBMENU as CascaderSubmenu<SignalSelectOptionType>,
  valueTypeToAvailableUnits: null,
  resolutionConfig: null,
  getResolutionConfigItem: () => null,
};

const ReqsReviewConfigContext = createContext<
  ReqsReviewConfigContextType | ReqsReviewConfigLoadingType
>(DEFAULT_VALUE);

export const useReqsReviewConfigContext = () => {
  const context = useContext(ReqsReviewConfigContext);
  if (!context) {
    throw new Error(
      'useReqsReviewConfigContext must be used within a ReqsReviewConfigProvider',
    );
  }
  return context;
};

type ReqsReviewConfigProviderProps = {
  children: React.ReactNode;
};

export const ReqsReviewConfigProvider = ({
  children,
}: ReqsReviewConfigProviderProps) => {
  const isRequirementsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );

  const { data: reqsReviewConfigData, isLoading } = useGetReqsReviewConfig({
    queryOptions: {
      enabled: isRequirementsReviewEnabled,
    },
  });

  const value = useMemo(() => {
    if (!isRequirementsReviewEnabled || isLoading || !reqsReviewConfigData) {
      return DEFAULT_VALUE;
    }

    const {
      filterConfigs: config,
      valueTypeToAvailableUnits,
      resolutionConfig,
    } = reqsReviewConfigData;
    const { configMap, labelPathMap } = createPathConfigMaps(config);
    const cascaderOptions = createCascaderOptions({ config });
    const mappedResolutionConfig =
      resolutionConfig.flat.reduce<ResolutionConfigMap>(
        (acc, item) => ({
          ...acc,
          [item.type]: item,
        }),
        // NB: This coercion is necessary because the .flat property type def doesn't by definition contain all possible
        // resolution types, but we can assume that the API will always return a complete set.
        {} as ResolutionConfigMap,
      );

    const out: ReqsReviewConfigContextType = {
      configMap,
      labelPathMap,
      cascaderOptions,
      valueTypeToAvailableUnits,
      resolutionConfig: {
        ...resolutionConfig,
        mapped: mappedResolutionConfig,
      },
      isLoading: false,
      getResolutionConfigItem: (resolutionType: ResolutionConfigItemType) =>
        mappedResolutionConfig[resolutionType] ?? null,
    };

    return out;
  }, [isRequirementsReviewEnabled, isLoading, reqsReviewConfigData]);

  return (
    <ReqsReviewConfigContext.Provider value={value}>
      {children}
    </ReqsReviewConfigContext.Provider>
  );
};
